// Setup UX pass 2026-10-21: the dense table and the row actions every Setup list shares. A row
// carries `group/row`; its actions sit in a `RowActions` cell that shows on hover or keyboard
// focus (always on touch screens), each a 28 px ghost icon button named after its row with a
// tooltip (Linear's and Vercel's settings lists).
import { Button, cn, Tooltip, TooltipContent, TooltipTrigger } from "@companyos/ui";
import type { ComponentProps, ComponentType, ReactNode } from "react";

/** `Table` className: 13 px text, ~40 px rows, 32 px column captions. */
export const DENSE_TABLE =
  "text-[13px] [&_td]:py-2 [&_th[scope=row]]:py-2 [&_th[scope=row]]:text-[13px] [&_thead_th]:h-8";

/** Hover / focus-within / touch visibility of a `group/row` row's actions. */
export const ROW_HOVER =
  "opacity-0 group-hover/row:opacity-100 group-focus-within/row:opacity-100 pointer-coarse:opacity-100";

/** The actions of a row, right-aligned, shown on hover or focus. */
export function RowActions({ className, ...props }: ComponentProps<"span">) {
  return (
    <span
      data-slot="row-actions"
      className={cn("flex items-center justify-end gap-0.5", ROW_HOVER, className)}
      {...props}
    />
  );
}

type IconType = ComponentType<{ className?: string; "aria-hidden"?: boolean }>;

export type RowActionProps = Omit<ComponentProps<typeof Button>, "children"> & {
  icon: IconType;
  /** The accessible name, naming the row (`Edit Recruiter`). */
  label: string;
  /** The tooltip; the label by default. */
  tip?: ReactNode;
};

/** A 28 px ghost icon button with a tooltip. */
export function RowAction({ icon: Icon, label, tip, className, ...props }: RowActionProps) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={label}
            className={cn("text-muted-foreground hover:text-foreground", className)}
            {...props}
          />
        }
      >
        <Icon aria-hidden={true} />
      </TooltipTrigger>
      <TooltipContent>{tip ?? label}</TooltipContent>
    </Tooltip>
  );
}
